/*
 * NOTICE:  All information contained herein is, and remains the property of Power Settlements Consulting and Software, LLC. 
 * The intellectual and technical concepts contained herein are proprietary to Power Settlements Consulting and Software, LLC 
 * and are protected by trade secret or copyright law. Dissemination or reproduction of this material, in whole or in part, 
 * is strictly forbidden without the prior written permission of Power Settlements Consulting and Software, LLC.
 * Copyright (c) Power Settlements Consulting and Software, LLC. All rights reserved.
*/

import { getMomentDateString } from '@/utils/dateUtil';
import { CISO_SAMC_API, TOOLS_API } from '@/api';
import { clone } from '@/utils/dataUtil';

const state = {
  uploadData: [],
  detailData: [],
  coordinators: [],
  selectedRows: [],
};
const actions = {
  async fetchCoordinators({ commit }) {
    try {
      const { data: { schedulingCoordinators } } = await TOOLS_API.get('/scheduling-coordinators');
      commit('setCoordinatorData', schedulingCoordinators);
    } catch (error) {
      this.$notify('Failed to load Scheduling Coordinators', 'error');
      console.log(error);
    }
  },
  async updateSelectedBDActiveFlag({ commit, state }, activeFlag) {
    const overrideRecords = clone(state.selectedRows);
    if (overrideRecords) {
      overrideRecords.forEach((bd) => { bd.activeFlag = activeFlag; });
      const params = {
        overrides: overrideRecords,
      };
      try {
        const { data: { data } } = await CISO_SAMC_API.put('/eim-sub-allocation/batch', params);
        commit('updateTable', data.overrides);
        this.$notify('Records Updated Successfully', 'success');
      } catch (error) {
        console.error(error);
        this.$notify('Records Updated Failed', 'error');
      }
    }
  },
  async fetchEimSubAllocationOverrideDetails({ commit }, request) {
    const params = {
      scs: request.marketParticipantsSelected?.toString(),
      startDate: request.tradeDateRangeSelected[0].toISOString(),
      endDate: request.tradeDateRangeSelected[1].toISOString(),
      version: request.templateOverrideVersionsSelected,
    };
    try {
      const { data: { data } } = await CISO_SAMC_API.get('/eim-sub-allocation/override-details', { params });
      data.forEach((x) => {
        x.tradingDate = getMomentDateString(x.tradingDate);
        x.inactivatedDate = getMomentDateString(x.inactivatedDate);
        x.updatedDate = getMomentDateString(x.updatedDate);
      });

      const headers = data.map(({
        importId,
        ba,
        scid,
        tradingDate,
        updatedBy,
        updatedDate,
        activeFlag,
        inactivatedDate,
        version,
      }) => ({
        importId,
        ba,
        scid,
        tradingDate,
        updatedBy,
        updatedDate,
        activeFlag,
        inactivatedDate,
        version,
      }));

      const distinctFunc = (arr, props = []) => [
        ...new Map(arr.map(x => [props.map(k => x[k]).join('|'), x])).values(),
      ];

      const distinctHeaders = distinctFunc(headers, ['tradingDate', 'version', 'ba']);

      commit('setUploadData', distinctHeaders);
      commit('setDetailData', data);
    } catch {
      this.$notify('Failed to retrieve Sub Allocation Override details', 'error');
    }
  },
  async addImportData({ commit, state }, fileData) {
    const importRecords = { overrides: fileData };
    try {
      await CISO_SAMC_API.post('/eim-sub-allocation/bd-override', importRecords);
      this.$notify('Overrides Imported', 'success');
    } catch (err) {
      console.error(err);
      this.$notify('Failed to import overrides', 'error');
    }
  },
  reset({ commit }) {
    commit('setUploadData', []);
    commit('setDetailData', []);
  },
};

const mutations = {
  setUploadData(state, value) {
    state.uploadData = value;
  },
  setDetailData(state, value) {
    state.detailData = value;
  },
  setCoordinatorData(state, value) {
    state.coordinators = value;
  },
  setSelectedRows(state, value) {
    state.selectedRows = value;
  },
  updateTable(state, items) {
    const uploadData = clone(state.uploadData);
    for (let i = 0; i < items.length; i++) {
      const item = uploadData.find((x) => x.importId === items[i].importId);
      if (item) {
        item.activeFlag = items[i].activeFlag;
      }
    }
    state.uploadData = uploadData;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
};